import { createElement, delegate } from '../../../core/dom/helpers.js';

let idCounter = 0;

/**
 * Mounts a tabbed field section layout.
 *
 * @type {import('../../../contracts/field-sections.js').FieldSectionLayoutMount}
 */
export function mount(container, config) {
  const uid = idCounter++;
  const { sections, sectionFields, unsectionedFields } = config;

  let activeKey = sections[0]?.key ?? null;

  // --- Build DOM ---
  const root = createElement('div', { class: 'field-sections field-sections--tabs' });

  // Tablist
  const tablist = createElement('div', {
    role: 'tablist',
    'aria-label': 'Form sections',
    class: 'field-sections-tablist',
  });

  /** @type {Map<string, HTMLButtonElement>} */
  const tabButtons = new Map();
  /** @type {Map<string, HTMLElement>} */
  const panels = new Map();

  for (let i = 0; i < sections.length; i++) {
    const sec = sections[i];
    const isActive = i === 0;
    const tabId = `fst-${uid}-${sec.key}`;
    const panelId = `fsp-${uid}-${sec.key}`;

    const tab = /** @type {HTMLButtonElement} */ (createElement('button', {
      type: 'button',
      role: 'tab',
      id: tabId,
      'aria-selected': String(isActive),
      'aria-controls': panelId,
      tabindex: isActive ? '0' : '-1',
      class: 'field-sections-tab',
    }, [sec.label]));
    tabButtons.set(sec.key, tab);
    tablist.appendChild(tab);

    const panel = createElement('div', {
      role: 'tabpanel',
      id: panelId,
      'aria-labelledby': tabId,
      class: 'field-sections-panel',
    });
    if (!isActive) {
      panel.hidden = true;
    }

    // Move field wrappers into panel
    const wrappers = sectionFields.get(sec.key) ?? [];
    for (const wrapper of wrappers) {
      panel.appendChild(wrapper);
    }

    panels.set(sec.key, panel);
  }

  root.appendChild(tablist);
  for (const sec of sections) {
    root.appendChild(/** @type {HTMLElement} */ (panels.get(sec.key)));
  }

  // Unsectioned fields stay in container before the section layout
  container.appendChild(root);

  // --- Tab switching ---
  /**
   * @param {string} key
   */
  function activate(key) {
    if (!tabButtons.has(key) || key === activeKey) return;

    // Deactivate current
    if (activeKey) {
      const prevTab = tabButtons.get(activeKey);
      const prevPanel = panels.get(activeKey);
      if (prevTab) {
        prevTab.setAttribute('aria-selected', 'false');
        prevTab.tabIndex = -1;
      }
      if (prevPanel) {
        prevPanel.hidden = true;
      }
    }

    // Activate new
    const nextTab = tabButtons.get(key);
    const nextPanel = panels.get(key);
    if (nextTab) {
      nextTab.setAttribute('aria-selected', 'true');
      nextTab.tabIndex = 0;
    }
    if (nextPanel) {
      nextPanel.hidden = false;
    }

    activeKey = key;
  }

  // --- Event delegation ---
  const cleanupClick = delegate(tablist, 'click', '.field-sections-tab', (_event, target) => {
    const key = sectionKeyFromTab(target);
    if (key) activate(key);
  });

  /**
   * @param {Element} tabEl
   * @returns {string | null}
   */
  function sectionKeyFromTab(tabEl) {
    for (const [key, btn] of tabButtons) {
      if (btn === tabEl) return key;
    }
    return null;
  }

  // --- Keyboard nav (roving tabindex) ---
  const sectionKeys = sections.map(s => s.key);

  /**
   * @param {KeyboardEvent} event
   */
  function handleKeydown(event) {
    const current = sectionKeys.indexOf(activeKey ?? '');
    if (current === -1) return;

    /** @type {number | null} */
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % sectionKeys.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + sectionKeys.length) % sectionKeys.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = sectionKeys.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    activate(sectionKeys[next]);
    tabButtons.get(sectionKeys[next])?.focus();
  }

  tablist.addEventListener('keydown', handleKeydown);

  // --- Handle ---
  return {
    activateSection(key) {
      activate(key);
    },

    getActiveSection() {
      return activeKey;
    },

    destroy() {
      // Remove listeners
      cleanupClick();
      tablist.removeEventListener('keydown', handleKeydown);

      // Restore field wrappers to container in deterministic normalized order
      for (const wrapper of unsectionedFields) {
        container.appendChild(wrapper);
      }
      for (const sec of sections) {
        const wrappers = sectionFields.get(sec.key) ?? [];
        for (const wrapper of wrappers) {
          container.appendChild(wrapper);
        }
      }

      // Remove layout chrome
      root.remove();
    },
  };
}
